import React from 'react';
import BlogArticleBase from '../BlogArticleBase';
import { planetNavItems } from './planetNavData';
import { ThemeNatalCTA, CompatibiliteAmoureuseCTA } from '../../CTA';

const MoonArticle = () => {
  // Données pour les fils d'Ariane structurés
  const breadcrumbs = [
    {
      name: "Accueil",
      path: "/"
    },
    {
      name: "Blog",
      path: "/blog"
    },
    {
      name: "Planètes",
      path: "/blog/planetes/"
    },
    {
      name: "La Lune en Astrologie",
      path: "/blog/planetes/lune"
    }
  ];

  // FAQ structurée pour le schema.org FAQPage
  const faqItems = [
    {
      question: "Que symbolise la Lune en astrologie?",
      answer: "La Lune en astrologie symbolise les émotions, l'inconscient, la mère, la féminité, les cycles, l'intuition, et nos besoins de sécurité et de protection."
    },
    {
      question: "Quelle est la signification de la Lune en Cancer?",
      answer: "La Lune en Cancer est considérée comme particulièrement puissante car le Cancer est son domicile naturel. Elle apporte une sensibilité extraordinaire, une intuition exceptionnelle et un fort instinct maternel/protecteur."
    },
    {
      question: "Que signifie la Lune en maison 4?",
      answer: "La Lune en maison 4 indique un attachement profond au foyer et à la famille, un lien puissant avec la mère, un besoin fondamental de racines et une mémoire émotionnelle intense."
    },
    {
      question: "Comment les phases lunaires affectent-elles l'astrologie?",
      answer: "Les phases lunaires créent un cycle d'énergie qui influence nos émotions et nos actions: la Nouvelle Lune favorise les nouveaux départs, la Pleine Lune amène les culminations et les prises de conscience, tandis que les phases décroissantes sont propices au lâcher-prise."
    }
  ];

  // Mots-clés optimisés pour le référencement
  const keywords = [
    "lune astrologie", "symbolisme lune", "thème natal lune", "cycles lunaires", 
    "lune en cancer", "lune maison 4", "phase lunaire naissance", "éclipse lunaire", 
    "lune noire", "aspects lune", "lune progressée", "synastrie lune"
  ];

  return (
    <BlogArticleBase
      title="La Lune en Astrologie: Symbolisme, Thème Natal et Cycles"
      description="Découvrez l'influence de la Lune en astrologie : symbolisme, thème natal et cycles émotionnels."
      slug="planetes/lune"
      image="https://astralight.xyz/images/planetes/lune.webp"
      publishDate="2025-05-17T10:00:00Z"
      modifiedDate="2025-05-17T10:00:00Z"
      author="Valtiel"
      category="Astrologie"
      navItems={planetNavItems}
      navTitle="Explorer les planètes"
      faqItems={faqItems}
      keywords={keywords}
      breadcrumbs={breadcrumbs}
      showTableOfContents={true}
    >
      {/* TL;DR - Résumé court au début */}
      <div className="article-summary">
        <p><strong>En bref:</strong> La Lune n'est pas qu'un astre dans le ciel - c'est le miroir de ton monde intérieur. Sa position dans ton thème natal dévoile tes besoins émotionnels profonds, tes réactions instinctives et ce que tu as hérité de ton passé. Ses cycles influencent tes humeurs quotidiennes et t'offrent un rythme naturel pour grandir et te transformer.</p>
      </div>

      <h2 id="definition">Qu'est-ce que la Lune en astrologie?</h2>
      <p>
        La Lune, ce fidèle satellite qui accompagne la Terre, n'est pas une simple roche flottant dans l'espace. En astrologie, elle joue un rôle fondamental. On la considère comme un "luminaire" au même titre que le Soleil, même si, techniquement, elle ne fait que refléter sa lumière. Et c'est justement ce qui la caractérise : elle reflète ce qui se cache en toi.
      </p>
      <p>
        Si ton Soleil représente qui tu es consciemment, ta Lune révèle ce que tu portes sous la surface. Elle parle de tes besoins les plus profonds, de tes réactions spontanées quand personne ne regarde, et de cette partie de toi qui se souvient encore de l'enfant que tu as été.
      </p>

      <h2 id="symbolisme">Symbolisme de la Lune dans l'astrologie</h2>
      <p>
        En astrologie, cette sphère argentée est comme une clé qui ouvre plusieurs portes de ta personnalité :
      </p>
      <ul>
        <li>Ton paysage émotionnel et tes fluctuations d'humeur</li>
        <li>Ton monde inconscient et la façon dont tu rêves</li>
        <li>Ton rapport à la figure maternelle et à ce qui nourrit</li>
        <li>Le féminin en toi (peu importe ton genre)</li>
        <li>Ta recherche de sécurité et comment tu te protèges</li>
        <li>Ces habitudes que tu ne peux pas expliquer, même à toi-même</li>
        <li>Les souvenirs qui restent gravés dans ta mémoire</li>
        <li>Cette voix intérieure qui te guide sans mots</li>
        <li>Ta capacité à t'adapter aux marées de la vie</li>
      </ul>
      
      <h2 id="theme-natal">La Lune dans le thème natal: ta carte émotionnelle</h2>
      <p>
        La position de la Lune dans ton thème natal n'est pas un détail - c'est un chapitre entier de ton histoire. Elle raconte comment tu traites ce que tu ressens, ce qui te fait te sentir en sécurité quand le monde semble hostile, et ces schémas que tu répètes sans même t'en apercevoir. Le signe et la maison où se trouve ta Lune sont comme des lunettes spéciales pour mieux te voir.
      </p>
      
      <h3 id="signes">La Lune dans les signes du zodiaque</h3>
      <p>
        Le signe dans lequel se trouve ta Lune colore profondément ta façon de ressentir et d'exprimer tes émotions. C'est comme une toile de fond pour ton monde intérieur. Découvre ce que cela signifie pour chaque signe :
      </p>
      
      <table>
        <caption>Caractéristiques émotionnelles de la Lune dans les signes du zodiaque</caption>
        <thead>
          <tr>
            <th scope="col">Lune en</th>
            <th scope="col">Caractéristiques émotionnelles</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><a href="/blog/signes/belier">Bélier</a></td>
            <td>Émotions spontanées et passionnées, besoin d'action pour se sentir bien, impatience émotionnelle, courage instinctif</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/taureau">Taureau</a></td>
            <td>Recherche de stabilité et de confort, émotions ancrées dans le concret, besoin de sécurité matérielle, fidélité émotionnelle</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/gemeaux">Gémeaux</a></td>
            <td>Émotions changeantes et intellectualisées, besoin constant de communication, curiosité émotionnelle, adaptabilité</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/cancer">Cancer</a></td>
            <td>Hypersensibilité et profondeur émotionnelle, fort instinct maternel/protecteur, attachement aux souvenirs, intuition puissante</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/lion">Lion</a></td>
            <td>Besoin de reconnaissance émotionnelle, générosité de cœur, fierté et dignité, besoin d'être au centre de l'attention</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/vierge">Vierge</a></td>
            <td>Émotions analysées et rationalisées, perfectionnisme émotionnel, sensibilité aux détails, besoin d'ordre pour se sentir en sécurité</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/balance">Balance</a></td>
            <td>Recherche d'harmonie émotionnelle, besoin de partage et d'équilibre, diplomatie instinctive, dépendance affective</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/scorpion">Scorpion</a></td>
            <td>Intensité émotionnelle, passion profonde, méfiance instinctive, résilience et transformation émotionnelle</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/sagittaire">Sagittaire</a></td>
            <td>Optimisme émotionnel, besoin d'expansion et d'aventure, philosophie de vie, émotions liées à la quête de sens</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/capricorne">Capricorne</a></td>
            <td>Retenue émotionnelle, sens des responsabilités, ambition comme sécurité, maturité émotionnelle précoce</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/verseau">Verseau</a></td>
            <td>Détachement émotionnel apparent, besoin d'originalité, émotions liées aux causes collectives, indépendance affective</td>
          </tr>
          <tr>
            <td><a href="/blog/signes/poissons">Poissons</a></td>
            <td>Hypersensibilité psychique, compassion naturelle, frontières émotionnelles floues, imagination et rêverie</td>
          </tr>
        </tbody>
      </table>
      
      {/* Ajout du CTA pour le thème natal à un emplacement stratégique */}
      <ThemeNatalCTA />
      
      <h3 id="lune-cancer">La Lune en Cancer: comme un poisson dans l'eau</h3>
      <p>
        Avoir la Lune en Cancer, c'est un peu comme posséder un superpouvoir émotionnel. Ce n'est pas un hasard : le Cancer est son domicile naturel, l'endroit où elle se sent parfaitement à l'aise. Si c'est ta position lunaire, tu navigues dans les eaux des émotions comme d'autres marchent sur la terre ferme.
      </p>
      <p>
        Avec cette configuration, tu as probablement ce besoin viscéral de créer un "nid" autour de toi, un espace sécurisant où accueillir ceux que tu aimes. Ta mémoire émotionnelle est comme un disque dur qui n'efface jamais vraiment ses données. Et oui, parfois, quand le monde devient trop bruyant ou menaçant, tu te replies dans ta carapace protectrice – pas parce que tu es asocial, mais parce que tu ressens tout avec une intensité que peu peuvent comprendre.
      </p>
      
      <h3 id="maisons">La Lune dans les maisons astrologiques</h3>
      <p>
        La maison où se trouve ta Lune est comme la pièce de ta vie où tu investis le plus d'émotions, l'endroit où tu cherches instinctivement refuge quand le monde semble trop chaotique :
      </p>
      
      <table>
        <caption>Influence de la Lune dans les différentes maisons astrologiques</caption>
        <thead>
          <tr>
            <th scope="col">Position de la Lune</th>
            <th scope="col">Signification</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Maison 1</td>
            <td>Sensibilité visible, émotions à fleur de peau, humeur changeante, forte intuition, besoin d'exprimer ses émotions ouvertement</td>
          </tr>
          <tr>
            <td>Maison 2</td>
            <td>Sécurité émotionnelle liée aux possessions matérielles, fluctuations financières, valeurs familiales fortes, confort par la stabilité</td>
          </tr>
          <tr>
            <td>Maison 3</td>
            <td>Communication émotionnelle, curiosité instinctive, lien fort avec la fratrie, apprentissage intuitif, besoin de partager ses idées</td>
          </tr>
          <tr>
            <td>Maison 4</td>
            <td>Attachement profond au foyer et à la famille, lien puissant avec la mère, besoin fondamental de racines, mémoire émotionnelle intense</td>
          </tr>
          <tr>
            <td>Maison 5</td>
            <td>Créativité émotionnelle, besoin d'attention affective, amour maternel pour ses enfants, plaisir dans l'expression de soi</td>
          </tr>
          <tr>
            <td>Maison 6</td>
            <td>Émotions liées au travail quotidien, sensibilité corporelle, besoin de se sentir utile, habitudes fluctuantes, service aux autres</td>
          </tr>
          <tr>
            <td>Maison 7</td>
            <td>Émotions projetées sur les partenaires, besoin de relation pour se sentir en sécurité, adaptabilité relationnelle, miroir émotionnel</td>
          </tr>
          <tr>
            <td>Maison 8</td>
            <td>Intensité émotionnelle, transformations cycliques, intuition profonde, lien avec l'invisible, attachement à l'intimité partagée</td>
          </tr>
          <tr>
            <td>Maison 9</td>
            <td>Quête émotionnelle de sens, voyages intuitifs, lien affectif aux croyances, besoin d'explorer pour se sentir en sécurité</td>
          </tr>
          <tr>
            <td>Maison 10</td>
            <td>Émotions liées à la carrière, sensibilité publique, figure maternelle importante, besoin de reconnaissance sociale</td>
          </tr>
          <tr>
            <td>Maison 11</td>
            <td>Émotions liées aux groupes, amitié comme famille, causes humanitaires, besoin d'appartenance collective, rêves communautaires</td>
          </tr>
          <tr>
            <td>Maison 12</td>
            <td>Subconscient émotionnel puissant, intuition psychique, rêves prophétiques, sensibilité cachée, lien au passé karmique</td>
          </tr>
        </tbody>
      </table>

      <h2 id="cycles">Cycles lunaires: la valse de tes humeurs</h2>
      <p>
        La Lune est une danseuse infatigable dans le ciel. Elle change de signe tous les 2-3 jours, créant ces rythmes qui semblent parfois orchestrer tes humeurs sans que tu en comprennes la partition. Cette valse mensuelle n'est pas un hasard - elle est l'un des plus anciens repères de l'humanité, un calendrier émotionnel gravé dans notre ADN collectif.
      </p>
      
      <figure>
        <img 
          src="/images/blog/phases_de_la_lune.webp"
          alt="Les huit phases lunaires en astrologie" 
          loading="lazy"
          width="1024"
        />
        <figcaption>Les huit phases principales du cycle lunaire et leur influence énergétique</figcaption>
      </figure>
      
      <div className="lunar-phases-description">
        <h3>Les huit phases lunaires et leur énergie</h3>
        <p>Chaque phase lunaire apporte sa propre énergie et influence ton monde intérieur de façon unique :</p>
        
        <ul className="phases-list">
          <li><strong>Nouvelle Lune</strong> : Le ciel est sombre, la Lune invisible. C'est le moment des nouveaux départs, des intentions et des graines plantées. L'énergie est introvertie, contemplative, tournée vers l'intérieur. C'est ta page blanche cosmique.</li>
          
          <li><strong>Premier Croissant</strong> : Un fin croissant apparaît. L'énergie est celle de l'émergence, des premiers pas, parfois hésitants mais nécessaires. Tu commences à voir les contours de tes projets, l'espoir grandit doucement.</li>
          
          <li><strong>Premier Quartier</strong> : La moitié du disque lunaire est visible. C'est le moment d'action, de décision, de surmonter les premiers obstacles. L'énergie est dynamique, déterminée. Le doute peut surgir, mais c'est normal – tu construis.</li>
          
          <li><strong>Lune Gibbeuse Croissante</strong> : Presque pleine, mais pas encore. L'énergie s'intensifie, tout accélère, les ajustements sont nécessaires. Tu sens que quelque chose grandit en toi, prend forme, mais demande encore des efforts.</li>
          
          <li><strong>Pleine Lune</strong> : Le disque lunaire complet brille dans le ciel. C'est le moment de plénitude, de révélation, parfois de tension émotionnelle. L'énergie est extériorisée, expansive, illuminante. Ce que tu as semé devient visible, pour le meilleur ou pour le plus déstabilisant.</li>
          
          <li><strong>Lune Gibbeuse Décroissante</strong> : Juste après la plénitude, le disque commence à décroître. C'est le temps du partage, de la gratitude, des premières intégrations. L'énergie commence à se tourner vers l'intérieur, plus réflexive.</li>
          
          <li><strong>Dernier Quartier</strong> : La moitié du disque est visible, mais à l'opposé du premier quartier. C'est le moment du lâcher-prise, de la libération de ce qui n'est plus nécessaire. L'énergie est celle du tri, du discernement. Tu comprends ce qui doit rester et ce qui doit partir.</li>
          
          <li><strong>Dernier Croissant</strong> : Un fin croissant à l'aube. L'énergie est celle de la dissolution, de l'acceptation profonde, du retour à l'essentiel. Tu te prépares déjà, parfois sans le savoir, au prochain cycle qui approche.</li>
        </ul>
        
        <p>Comme le disait l'astronome Johannes Kepler : "La Lune ne brille pas de sa propre lumière, mais de celle qu'elle reçoit du Soleil." De la même façon, chaque phase lunaire reflète une partie différente de ton voyage intérieur, illuminant certains aspects de ta conscience selon un rythme aussi ancien que la vie sur Terre.</p>
      </div>
      
      <h2 id="phases">Les phases lunaires et ta personnalité</h2>
      <p>
        Tu es né sous une certaine phase lunaire - c'est la relation qu'entretenaient le Soleil et la Lune au moment précis où tu as poussé ton premier cri. Cette configuration n'est pas anodine. Elle raconte comment ton côté conscient (Soleil) et ton monde émotionnel (Lune) collaborent ou se défient.
      </p>
      <p>
        D'après <a href="https://eclipse.gsfc.nasa.gov/lunar.html" target="_blank" rel="noopener noreferrer">la NASA</a>, ces phénomènes astronomiques se déroulent selon un cycle parfaitement réglé. En astrologie, on leur attribue une influence sur le développement de ta personnalité - un peu comme si tu étais né dans une saison émotionnelle particulière.
      </p>
      
      <h2 id="eclipses">Les éclipses lunaires: quand le rideau se lève</h2>
      <p>
        Les éclipses lunaires sont ces moments où la Terre s'interpose entre le Soleil et la Lune, créant un spectacle céleste fascinant. Mais en astrologie, elles sont bien plus qu'un phénomène visuel - elles sont des révélateurs puissants qui dévoilent ce qui se cachait dans l'ombre de ton inconscient.
      </p>
      <p>
        C'est un peu comme si quelqu'un allumait soudain la lumière dans une pièce que tu traversais à tâtons. Tu découvres ce qui t'entourait réellement, pour le meilleur et parfois pour le plus déstabilisant. Ces moments peuvent précipiter des prises de conscience, des lâcher-prises, des fins de cycle nécessaires pour avancer.
      </p>
      
      <h2 id="lune-noire">La Lune noire (Lilith) et la Lune: le duo intérieur</h2>
      <p>
        Dans ton thème natal, il existe une tension fascinante entre ta Lune et ce qu'on appelle la Lune noire (Lilith). Si ta Lune est cette partie de toi qui cherche confort et sécurité, Lilith est cette voix rebelle qui refuse les compromis, même quand ils seraient plus faciles.
      </p>
      <p>
        C'est comme avoir deux conseillères intérieures - l'une qui te dit "Trouve ta place, adapte-toi, crée des liens", et l'autre qui murmure "Reste sauvage, ne te plie pas aux attentes, sois authentique à tout prix". Observer cette dynamique, c'est souvent comprendre pourquoi certaines situations te plongent dans des contradictions émotionnelles si profondes.
      </p>
      
      <h2 id="aspects">Aspects lunaires: les nuances de ton monde intérieur</h2>
      <p>
        La Lune ne navigue jamais seule dans ton thème natal. Elle forme des angles (aspects) avec d'autres planètes, créant des conversations cosmiques qui colorent ton paysage émotionnel. C'est comme si chaque aspect ajoutait un filtre particulier à l'objectif avec lequel tu perçois et traites tes émotions :
      </p>
      <table>
        <caption>Impact des aspects lunaires sur notre monde émotionnel</caption>
        <thead>
          <tr>
            <th scope="col">Aspect</th>
            <th scope="col">Influence émotionnelle</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Lune-Soleil</td>
            <td>Harmonie ou tension entre les besoins émotionnels et l'identité consciente, relation aux figures parentales</td>
          </tr>
          <tr>
            <td>Lune-Mercure</td>
            <td>Communication des émotions, pensées influencées par les sentiments, mémoire émotionnelle</td>
          </tr>
          <tr>
            <td>Lune-Vénus</td>
            <td>Expression de l'affection, besoins émotionnels dans l'amour, relation aux plaisirs et au confort</td>
          </tr>
          <tr>
            <td>Lune-Mars</td>
            <td>Réactions émotionnelles instinctives, passion, colère, désir, énergie émotionnelle</td>
          </tr>
          <tr>
            <td>Lune-Jupiter</td>
            <td>Expansion émotionnelle, optimisme, générosité affective, croissance par les émotions</td>
          </tr>
          <tr>
            <td>Lune-Saturne</td>
            <td>Restrictions émotionnelles, sécurité par la structure, responsabilité affective, maturité émotionnelle</td>
          </tr>
          <tr>
            <td>Lune-Uranus</td>
            <td>Émotions imprévisibles, besoin de liberté émotionnelle, originalité, intuitions fulgurantes</td>
          </tr>
          <tr>
            <td>Lune-Neptune</td>
            <td>Sensibilité psychique, empathie profonde, confusion émotionnelle, imagination riche</td>
          </tr>
          <tr>
            <td>Lune-Pluton</td>
            <td>Intensité émotionnelle extrême, transformations profondes, pouvoir émotionnel, obsessions</td>
          </tr>
        </tbody>
      </table>
      
      <h2 id="lune-progressee">La Lune progressée: ton voyage émotionnel</h2>
      <p>
        Tu as peut-être remarqué que tu n'es plus la même personne qu'il y a cinq ou dix ans. La Lune progressée raconte cette évolution. Elle change de signe tous les 2,5 ans environ, comme si tu entrais dans une nouvelle saison émotionnelle.
      </p>
      <p>
        C'est fascinant d'observer ces transitions. Peut-être qu'après une période "Taureau" où la stabilité était ta priorité, tu entres dans une phase "Gémeaux" où la curiosité et la communication deviennent centrales. Ces cycles ne sont pas des hasards - ils constituent la trame de ton développement personnel, les chapitres de ton histoire émotionnelle.
      </p>
      
      <h2 id="synastrie">La Lune dans la synastrie: l'alchimie invisible</h2>
      <p>
        Quand tu rencontres quelqu'un, vos Lunes se rencontrent aussi. Dans l'analyse des relations (synastrie), la Lune est souvent cette clé invisible qui explique pourquoi tu te sens "chez toi" avec certaines personnes, et étranger avec d'autres, sans raison apparente.
      </p>
      <p>
        Si la Lune de ton partenaire forme des aspects harmonieux avec tes planètes personnelles, vous pouvez ressentir cette connexion inexplicable, cette impression de vous comprendre sans mots. À l'inverse, des tensions à ce niveau peuvent créer ces situations où vous parlez la même langue, mais ne semblez jamais vous comprendre vraiment.
      </p>
      
      {/* Ajout du CTA pour la compatibilité amoureuse */}
      <CompatibiliteAmoureuseCTA />
      
      <h2 id="pratique">Comment danser avec l'énergie lunaire</h2>
      <p>
        La Lune n'est pas qu'un concept abstrait - elle peut devenir une véritable alliée dans ton quotidien. Voici quelques façons d'harmoniser ta vie avec ses cycles et son énergie :
      </p>
      <ul>
        <li>Note tes humeurs et intuitions pendant les différentes phases lunaires - des motifs révélateurs pourraient émerger</li>
        <li>Crée des rituels de soin personnel qui respectent ton rythme naturel, particulièrement autour de la Pleine et de la Nouvelle Lune</li>
        <li>Utilise la pleine Lune pour célébrer ce qui a fleuri dans ta vie et libérer ce qui t'encombre</li>
        <li>Profite de la Nouvelle Lune pour planter les graines de tes intentions et projets</li>
        <li>Apprends à honorer tes besoins émotionnels sans les juger - ils sont ta boussole intérieure</li>
        <li>Porte attention à tes rêves, surtout autour de la Pleine Lune - ils peuvent contenir des messages précieux</li>
        <li>Aménage ton espace pour qu'il nourrisse les besoins de ton signe lunaire</li>
      </ul>
      
      <blockquote>
        <p>
          "La Lune nous enseigne l'art de la transformation et du renouveau. À travers ses cycles constants, elle nous rappelle que tout change, que rien n'est permanent, et que c'est dans cette impermanence même que réside la beauté de la vie." — Demetra George, <cite>Mysteries of the Dark Moon</cite>
        </p>
      </blockquote>
      
      <h2 id="conclusion">L'énergie lunaire: ton miroir intérieur</h2>
      <p>
        La Lune n'est pas qu'un astre parmi d'autres dans ton thème natal - elle est ce miroir qui reflète les parties de toi que tu ne montres pas toujours au monde. Elle garde la mémoire de l'enfant que tu as été, des larmes que tu as versées, des joies qui t'ont submergé. Elle est cette présence silencieuse qui t'accompagne dans tes nuits d'insomnie comme dans tes moments de plénitude.
      </p>
      <p>
        En apprenant à reconnaître son influence dans ta vie, tu ne deviens pas esclave de ses cycles - tu deviens plus conscient de tes propres marées intérieures. C'est peut-être ça, la véritable sagesse lunaire : comprendre que nos émotions, comme elle, traversent des phases, et que chacune d'elles - même les plus sombres - fait partie d'un cycle plus grand qui nous transforme, nous renouvelle et nous ramène toujours à notre pleine lumière.
      </p>
    </BlogArticleBase>
  );
};

export default MoonArticle; 